import type { SyncStreamSubscription } from "@powersync/web";
import { powerSyncDb } from "@/lib/powersync";
import { rateCalendarMonths } from "@/lib/sync-window";

/*
 * Subscriptions to `selected_property`, one per property the user belongs to.
 *
 * There is no `selected_org` stream: the property is the tenant, and the
 * catalog/cross-property data syncs via `bootstrap` + `my_operations`, and a
 * user's own support conversations via `my_support` (all auto-subscribed).
 *
 * Every property is subscribed, not just the active one: the comms inbox,
 * unread counts and activity all read across the whole portfolio by default
 * (`propertyNarrowing` returns undefined until the user narrows the scope), so
 * subscribing only the active property would empty them. What bounds the cost
 * is the `months` window on `rate_calendar` and the per-property TTL below,
 * not a narrower subscription set.
 */

const SECONDS_PER_DAY = 86_400;

/**
 * How long a non-primary property's data survives on the device after its last
 * subscription is dropped — which happens when the user loses access to it, or
 * signs out.
 *
 * Long enough that losing and regaining access (or a membership sync blip)
 * doesn't force a re-download, short enough that the space comes back.
 */
const SECONDARY_PROPERTY_TTL_SECONDS = 7 * SECONDS_PER_DAY;

interface PropertySubscription {
  /**
   * The month window {@link sub} was subscribed with. PowerSync fixes
   * subscription parameters at subscribe time, so a rolled window has to
   * re-subscribe rather than mutate.
   *
   * Deliberately NOT keyed on the TTL as well. The TTL is chosen once, when
   * the property is first subscribed, and a later change of primary does not
   * re-subscribe: dropping a live subscription to hand the same stream a
   * different TTL empties its buckets on the device for as long as the new
   * subscription takes to first-sync, and the primary resolves a beat after
   * the property list does — so keying on it would put that gap in every
   * single boot. TTL only decides how long data survives AFTER the last
   * subscription is dropped, so getting it slightly stale costs nothing while
   * the app is running.
   */
  months: string;
  sub: SyncStreamSubscription;
}

const propertySubscriptions = new Map<string, PropertySubscription>();

/**
 * The property the user is currently working in, owned here rather than passed
 * per call: callers that just want a property synced — the guest drawer
 * resolving a chat's property, say — must not be able to decide its TTL, or an
 * ad-hoc call would demote the primary property and the next render promote it
 * back, re-subscribing both times.
 */
let primaryPropertyId: string | null = null;

/**
 * Read once, when the property is first subscribed. On a warm start the
 * session already names the active property and it is pinned; on a first-ever
 * sync the catalog can land before the session resolves, and the property is
 * then held on the finite TTL like any other. Both are fine — the TTL governs
 * what survives after the last subscription is dropped, and the property the
 * user is working in keeps a live subscription the whole time.
 */
function ttlFor(propertyId: string): number {
  return propertyId === primaryPropertyId
    ? Number.POSITIVE_INFINITY
    : SECONDARY_PROPERTY_TTL_SECONDS;
}

function rememberPropertySub(
  propertyId: string,
  next: PropertySubscription
): void {
  const prev = propertySubscriptions.get(propertyId);
  if (prev) {
    prev.sub.unsubscribe();
  }
  propertySubscriptions.set(propertyId, next);
}

/**
 * Ensure the sync stream for the given property is active; wait until its
 * first sync completes.
 */
export async function ensureSelectedPropertyStream(
  propertyId: string
): Promise<void> {
  // The window is read here, at subscribe time, so it rolls on the next cold
  // start or selection change after the month turns. Deliberately not driven
  // off a timer: the window already reaches a year ahead, so a late roll only
  // delays dropping the trailing month — nothing a screen can observe.
  const parameters = {
    months: rateCalendarMonths(),
    property_id: propertyId,
  };
  const months = parameters.months.join(",");

  let active = propertySubscriptions.get(propertyId);
  if (!active || active.months !== months) {
    const stream = powerSyncDb.syncStream("selected_property", parameters);
    // Subscribe before dropping the previous subscription, so re-keying never
    // leaves the property momentarily unsubscribed.
    const sub = await stream.subscribe({ ttl: ttlFor(propertyId) });
    active = { months, sub };
    rememberPropertySub(propertyId, active);
  }
  await active.sub.waitForFirstSync();
}

/**
 * The `rate_calendar` months this session actually subscribed for, or null
 * before the property has a stream.
 *
 * Read this rather than recomputing the window from the clock. The two agree
 * until the month turns under a session left open, and then the clock names a
 * month the stream never requested: a screen trusting it would show rows that
 * are blank for want of data as though they were blank for want of a value.
 */
export function subscribedRateCalendarMonths(
  propertyId: string
): string[] | null {
  const active = propertySubscriptions.get(propertyId);
  return active ? active.months.split(",") : null;
}

/**
 * Ensure sync streams for ALL properties in the selection. Diffs against
 * the previous selection: subscribes to newly-added properties, unsubscribes
 * properties that are no longer selected. Returns once every new
 * subscription has first-synced.
 *
 * Unsubscribing is what starts a property's TTL ticking — PowerSync only
 * counts down once no subscription object is attached — so dropping a property
 * here is what eventually reclaims its rows.
 */
export async function ensureSelectedPropertiesStreams(
  propertyIds: string[],
  primary: string | null
) {
  primaryPropertyId = primary;
  const desired = new Set(propertyIds);

  // Unsubscribe from properties no longer in the selection.
  for (const [propertyId, active] of propertySubscriptions.entries()) {
    if (!desired.has(propertyId)) {
      active.sub.unsubscribe();
      propertySubscriptions.delete(propertyId);
    }
  }

  // Subscribe to any new properties + wait for their first sync in parallel.
  await Promise.all(
    propertyIds.map((propertyId) => ensureSelectedPropertyStream(propertyId))
  );
}
